import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, GripVertical, Instagram, Youtube, Eye, EyeOff, Upload, Link } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSocial = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();
  const fileRef = useRef(null);

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    platform: 'instagram',
    postUrl: '',
    imageUrl: '',
    caption: '',
    order: 0,
    active: true
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/social-posts/all`, { headers });
      setPosts(res.data);
    } catch (err) {
      console.error('Error fetching social posts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setForm({ platform: 'instagram', postUrl: '', imageUrl: '', caption: '', order: 0, active: true });
    setEditingPost(null);
  };

  const openCreate = () => {
    resetForm();
    setForm(f => ({ ...f, order: posts.length }));
    setShowDialog(true);
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setForm({
      platform: post.platform || 'instagram',
      postUrl: post.postUrl || '',
      imageUrl: post.imageUrl || '',
      caption: post.caption || '',
      order: post.order || 0,
      active: post.active !== false
    });
    setShowDialog(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await axios.post(`${API}/upload/image`, formData, { headers });
      setForm(f => ({ ...f, imageUrl: res.data.url }));
      toast({ title: 'Image uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.imageUrl && !form.postUrl) {
      toast({ title: 'Please add an image or post URL', variant: 'destructive' });
      return;
    }
    try {
      if (editingPost) {
        await axios.put(`${API}/social-posts/${editingPost._id}`, form, { headers });
        toast({ title: 'Post updated' });
      } else {
        await axios.post(`${API}/social-posts`, form, { headers });
        toast({ title: 'Post created' });
      }
      setShowDialog(false);
      resetForm();
      fetchPosts();
    } catch (err) {
      toast({ title: 'Error saving post', variant: 'destructive' });
    }
  };

  const toggleActive = async (post) => {
    try {
      await axios.put(`${API}/social-posts/${post._id}`, { active: !post.active }, { headers });
      fetchPosts();
    } catch (err) {
      toast({ title: 'Error updating post', variant: 'destructive' });
    }
  };

  const deletePost = async (post) => {
    if (!window.confirm('Delete this social post?')) return;
    try {
      await axios.delete(`${API}/social-posts/${post._id}`, { headers });
      toast({ title: 'Post deleted' });
      fetchPosts();
    } catch (err) {
      toast({ title: 'Error deleting post', variant: 'destructive' });
    }
  };

  const platformIcon = (platform) => {
    if (platform === 'youtube') return <Youtube size={16} className="text-red-500" />;
    return <Instagram size={16} className="text-pink-500" />;
  };

  return (
    <div className="bg-black text-white pt-24 min-h-screen" data-testid="admin-social-page">
      <div className="container mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}
              className="border-gray-600 text-gray-300 hover:border-[#F4B400] hover:text-[#F4B400]">
              <ArrowLeft size={16} className="mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Social Media Posts</h1>
              <p className="text-gray-400 text-sm">Manage Instagram & YouTube content shown on the website</p>
            </div>
          </div>
          <Button onClick={openCreate} className="bg-[#F4B400] text-black hover:bg-[#d49f00]" data-testid="add-social-post-btn">
            <Plus size={18} className="mr-2" /> Add Post
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#F4B400] mx-auto" />
          </div>
        ) : posts.length === 0 ? (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="p-12 text-center">
              <Instagram className="mx-auto mb-4 text-gray-500" size={48} />
              <h3 className="text-xl font-bold mb-2">No Social Posts Yet</h3>
              <p className="text-gray-400 mb-6">Add Instagram or YouTube posts to showcase on your website.</p>
              <Button onClick={openCreate} className="bg-[#F4B400] text-black hover:bg-[#d49f00]">
                <Plus size={18} className="mr-2" /> Add First Post
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="social-posts-list">
            {posts.map((post) => (
              <Card key={post._id} className={`bg-white/5 border-white/10 overflow-hidden ${!post.active ? 'opacity-50' : ''}`}>
                <div className="relative aspect-square bg-gray-900">
                  {post.imageUrl ? (
                    <img src={post.imageUrl} alt={post.caption || 'Post'} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {platformIcon(post.platform)}
                    </div>
                  )}
                  <div className="absolute top-3 left-3 flex gap-2">
                    <Badge className={post.active ? 'bg-green-600 text-white' : 'bg-gray-600 text-white'}>
                      {post.active ? 'Active' : 'Hidden'}
                    </Badge>
                    <Badge className="bg-black/60 text-white flex items-center gap-1">
                      {platformIcon(post.platform)}
                      {post.platform}
                    </Badge>
                  </div>
                  <div className="absolute top-3 right-3 text-white bg-black/60 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                    {post.order}
                  </div>
                </div>
                <CardContent className="p-4">
                  {post.caption && <p className="text-gray-300 text-sm line-clamp-2 mb-3">{post.caption}</p>}
                  {post.postUrl && (
                    <a href={post.postUrl} target="_blank" rel="noopener noreferrer" className="text-[#F4B400] text-xs hover:underline flex items-center gap-1 mb-3">
                      <Link size={12} /> View Original
                    </a>
                  )}
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(post)}
                      className="flex-1 border-gray-600 text-gray-300 hover:border-[#F4B400] hover:text-[#F4B400]">
                      Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => toggleActive(post)}
                      className="border-gray-600 text-gray-300 hover:border-[#F4B400] hover:text-[#F4B400]">
                      {post.active ? <EyeOff size={14} /> : <Eye size={14} />}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => deletePost(post)}
                      className="border-red-800 text-red-400 hover:bg-red-900 hover:text-red-300">
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingPost ? 'Edit Social Post' : 'Add Social Post'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Platform</Label>
              <Select value={form.platform} onValueChange={(v) => setForm(f => ({ ...f, platform: v }))}>
                <SelectTrigger className="mt-1 bg-gray-800 border-gray-700"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="youtube">YouTube</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Image</Label>
              {form.imageUrl && (
                <div className="mt-2 mb-2 relative w-full aspect-video rounded-lg overflow-hidden bg-gray-800">
                  <img src={form.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex gap-2 mt-1">
                <input type="file" ref={fileRef} accept="image/*" className="hidden" onChange={handleImageUpload} />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="border-gray-600 text-gray-300 hover:border-[#F4B400] hover:text-[#F4B400]">
                  <Upload size={14} className="mr-2" /> {uploading ? 'Uploading...' : 'Upload Image'}
                </Button>
              </div>
            </div>

            <div>
              <Label>Post URL (link to original post)</Label>
              <Input className="mt-1 bg-gray-800 border-gray-700" value={form.postUrl}
                onChange={(e) => setForm(f => ({ ...f, postUrl: e.target.value }))}
                placeholder="https://www.instagram.com/p/..." />
            </div>

            <div>
              <Label>Caption</Label>
              <Textarea className="mt-1 bg-gray-800 border-gray-700" value={form.caption} rows={3}
                onChange={(e) => setForm(f => ({ ...f, caption: e.target.value }))}
                placeholder="Brief description..." />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Display Order</Label>
                <Input className="mt-1 bg-gray-800 border-gray-700" type="number" value={form.order}
                  onChange={(e) => setForm(f => ({ ...f, order: parseInt(e.target.value) || 0 }))} />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.active}
                    onChange={(e) => setForm(f => ({ ...f, active: e.target.checked }))}
                    className="w-4 h-4 accent-[#F4B400]" />
                  <span className="text-sm text-gray-300">Active (visible on site)</span>
                </label>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={handleSubmit} className="flex-1 bg-[#F4B400] text-black hover:bg-[#d49f00]" data-testid="save-social-post-btn">
                {editingPost ? 'Update Post' : 'Create Post'}
              </Button>
              <Button variant="outline" onClick={() => setShowDialog(false)}
                className="border-gray-600 text-gray-300 hover:border-white hover:text-white">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSocial;
